import React, { useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { MapContainer, TileLayer, CircleMarker, Popup, useMap } from 'react-leaflet';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Checkbox } from '@/components/ui/checkbox';
import { ScrollArea } from '@/components/ui/scroll-area';
import { 
  MapPin, Play, Pause, SplitSquareVertical, Calculator, 
  Eye, ArrowLeft, Check, X, Loader2, AlertTriangle, Layers 
} from 'lucide-react';
import { formatCurrency } from '@/lib/utils';
import TimeAnimationControls from '@/components/TimeAnimationControls';
import WhatIfScenario from '@/components/WhatIfScenario';
import SplitScreenMap from '@/components/SplitScreenMap';
import 'leaflet/dist/leaflet.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const US_CENTER = [39.8, -98.5];
const DEFAULT_ZOOM = 4;

const getMarkerColor = (score) => {
  if (score >= 70) return '#229644';
  if (score >= 50) return '#FFA500';
  return '#FF521C';
};

const getMarkerSize = (value) => {
  if (value >= 1000000) return 12;
  if (value >= 500000) return 10;
  if (value >= 200000) return 8;
  return 6;
};

function FitBoundsOnLoad({ features }) {
  const map = useMap();
  React.useEffect(() => {
    if (features && features.length > 0) {
      const bounds = features.map(f => [
        f.geometry.coordinates[1],
        f.geometry.coordinates[0]
      ]);
      if (bounds.length > 0) {
        map.fitBounds(bounds, { padding: [50, 50], maxZoom: 6 });
      }
    }
  }, [features, map]);
  return null;
}

export default function AdvancedMapView() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('standard');
  const [selectedContracts, setSelectedContracts] = useState([]);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentMonth, setCurrentMonth] = useState(0);
  const [timelineFeatures, setTimelineFeatures] = useState(null);
  const [timelineLabel, setTimelineLabel] = useState('');

  const { data: geoData, isLoading } = useQuery({
    queryKey: ['geo-contracts'],
    queryFn: async () => {
      const token = localStorage.getItem('ccip_token');
      const response = await axios.get(`${BACKEND_URL}/api/geo/contracts`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return response.data;
    },
  });

  const handleViewDetails = (contractId) => {
    navigate(`/contracts/${contractId}`);
  };

  const toggleContractSelection = (contractId) => {
    setSelectedContracts(prev => 
      prev.includes(contractId) 
        ? prev.filter(id => id !== contractId)
        : [...prev, contractId]
    );
  };

  const selectAllVisible = () => {
    const features = activeTab === 'timeline' && timelineFeatures 
      ? timelineFeatures.features 
      : geoData?.features || [];
    const allIds = features.map(f => f.properties.contractId);
    setSelectedContracts(allIds);
  };

  const clearSelection = () => {
    setSelectedContracts([]);
  };

  const handleTimelineChange = useCallback((featureCollection, label) => {
    setTimelineFeatures(featureCollection);
    setTimelineLabel(label);
  }, []);

  const currentFeatures = activeTab === 'timeline' && timelineFeatures 
    ? timelineFeatures.features 
    : geoData?.features || [];

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-[#09409C]" />
      </div>
    );
  }

  return (
    <div className="h-screen flex flex-col bg-gray-50" data-testid="advanced-map-view">
      {/* Header */}
      <div className="bg-white border-b p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="sm" onClick={() => navigate('/executive')}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Dashboard
            </Button>
            <div>
              <h1 className="text-2xl font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>
                <span className="text-gradient-primary">Advanced Map Analysis</span>
              </h1>
              <p className="text-sm text-muted-foreground">
                Geographic visualization with time animation, comparison, and scenario modeling
              </p>
            </div>
          </div>
          
          {/* Selection Controls */}
          {activeTab !== 'split' && (
            <div className="flex items-center gap-2">
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => navigate('/map/territory')}
                className="mr-2"
              >
                <Layers className="w-4 h-4 mr-1" />
                Territory Analysis
              </Button>
              <Badge variant="outline">
                {selectedContracts.length} selected
              </Badge>
              <Button variant="outline" size="sm" onClick={selectAllVisible}>
                <Check className="w-4 h-4 mr-1" />
                Select All
              </Button>
              <Button variant="outline" size="sm" onClick={clearSelection}>
                <X className="w-4 h-4 mr-1" />
                Clear
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="bg-white border-b px-4">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="h-12">
            <TabsTrigger value="standard" className="flex items-center gap-2">
              <MapPin className="w-4 h-4" />
              Standard View
            </TabsTrigger>
            <TabsTrigger value="timeline" className="flex items-center gap-2">
              <Play className="w-4 h-4" />
              Time Animation
            </TabsTrigger>
            <TabsTrigger value="split" className="flex items-center gap-2">
              <SplitSquareVertical className="w-4 h-4" />
              Split Comparison
            </TabsTrigger>
            <TabsTrigger value="scenario" className="flex items-center gap-2">
              <Calculator className="w-4 h-4" />
              What-If Scenario
            </TabsTrigger>
          </TabsList>
        </Tabs>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex overflow-hidden">
        {/* Standard View */}
        {activeTab === 'standard' && (
          <div className="flex-1 flex">
            <div className="flex-1 relative">
              <MapContainer
                center={US_CENTER}
                zoom={DEFAULT_ZOOM}
                style={{ height: '100%', width: '100%' }}
              >
                <TileLayer
                  attribution='&copy; OpenStreetMap'
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                />
                <FitBoundsOnLoad features={currentFeatures} />
                
                {currentFeatures.map((feature, idx) => {
                  const [lng, lat] = feature.geometry.coordinates;
                  const props = feature.properties;
                  const isSelected = selectedContracts.includes(props.contractId);
                  const color = getMarkerColor(props.competitivenessScore);
                  const size = getMarkerSize(props.annualValue);
                  
                  return (
                    <CircleMarker
                      key={props.contractId || idx}
                      center={[lat, lng]}
                      radius={size}
                      pathOptions={{
                        color: isSelected ? '#09409C' : color,
                        fillColor: color,
                        fillOpacity: isSelected ? 0.9 : 0.7,
                        weight: isSelected ? 3 : 2,
                      }}
                      eventHandlers={{
                        click: () => toggleContractSelection(props.contractId),
                      }}
                    >
                      <Popup>
                        <div className="min-w-[200px]">
                          <div className="flex items-center gap-2 mb-2">
                            <Checkbox 
                              checked={isSelected}
                              onCheckedChange={() => toggleContractSelection(props.contractId)}
                            />
                            <div>
                              <div className="font-semibold text-sm">{props.accountName}</div>
                              <div className="text-xs text-muted-foreground">{props.contractNumber}</div>
                            </div>
                          </div>
                          <div className="space-y-1 text-xs">
                            <div className="flex justify-between">
                              <span>Value:</span>
                              <span className="font-medium">{formatCurrency(props.annualValue)}</span>
                            </div>
                            <div className="flex justify-between">
                              <span>Health:</span>
                              <Badge className={`text-[10px] ${props.competitivenessScore >= 70 ? 'bg-green-100 text-green-800' : props.competitivenessScore >= 50 ? 'bg-yellow-100 text-yellow-800' : 'bg-red-100 text-red-800'}`}>
                                {props.competitivenessScore}%
                              </Badge>
                            </div>
                            <div className="flex justify-between">
                              <span>Expires:</span>
                              <span>{props.daysToExpiration} days</span>
                            </div>
                          </div>
                          <Button 
                            size="sm" 
                            className="w-full mt-2 h-7 text-xs"
                            onClick={() => handleViewDetails(props.contractId)}
                          >
                            <Eye className="w-3 h-3 mr-1" />
                            View Details
                          </Button>
                        </div>
                      </Popup>
                    </CircleMarker>
                  );
                })}
              </MapContainer>

              {/* Legend */}
              <div className="absolute bottom-4 right-4 z-[1000] bg-white/95 backdrop-blur-sm rounded-lg shadow-lg p-3 text-xs">
                <div className="font-medium mb-2">Contract Health</div>
                <div className="space-y-1">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-[#229644]" />
                    <span>Healthy (70%+)</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-[#FFA500]" />
                    <span>At Risk (50-69%)</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-[#FF521C]" />
                    <span>Critical (&lt;50%)</span>
                  </div>
                </div>
              </div>

              {/* Stats */}
              <div className="absolute bottom-4 left-4 z-[1000] bg-white/95 backdrop-blur-sm rounded-lg shadow-lg p-3 text-xs">
                <div className="grid grid-cols-2 gap-x-4 gap-y-1">
                  <div className="flex items-center gap-1">
                    <MapPin className="w-3 h-3 text-[#09409C]" />
                    <span className="font-medium">{currentFeatures.length}</span>
                    <span className="text-muted-foreground">contracts</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <span className="font-medium text-[#229644]">
                      {formatCurrency(currentFeatures.reduce((sum, f) => sum + f.properties.annualValue, 0))}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Selected Contracts Sidebar */}
            {selectedContracts.length > 0 && (
              <div className="w-80 border-l bg-white">
                <div className="p-4 border-b">
                  <h3 className="font-semibold">Selected Contracts</h3>
                  <p className="text-xs text-muted-foreground">
                    {selectedContracts.length} contracts selected for analysis
                  </p>
                </div>
                <ScrollArea className="h-[calc(100%-80px)]">
                  <div className="p-2 space-y-2">
                    {currentFeatures
                      .filter(f => selectedContracts.includes(f.properties.contractId))
                      .map(f => (
                        <Card key={f.properties.contractId} className="p-3">
                          <div className="flex justify-between items-start">
                            <div>
                              <div className="font-medium text-sm">{f.properties.accountName}</div>
                              <div className="text-xs text-muted-foreground">{f.properties.contractNumber}</div>
                            </div>
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              className="h-6 w-6 p-0"
                              onClick={() => toggleContractSelection(f.properties.contractId)}
                            >
                              <X className="w-3 h-3" />
                            </Button>
                          </div>
                          <div className="flex items-center gap-2 mt-2">
                            <Badge variant="outline" className="text-[10px]">
                              {formatCurrency(f.properties.annualValue)}
                            </Badge>
                            <Badge className={`text-[10px] ${f.properties.competitivenessScore >= 70 ? 'bg-green-100 text-green-800' : f.properties.competitivenessScore >= 50 ? 'bg-yellow-100 text-yellow-800' : 'bg-red-100 text-red-800'}`}>
                              {f.properties.competitivenessScore}%
                            </Badge>
                          </div>
                        </Card>
                      ))}
                  </div>
                </ScrollArea>
              </div>
            )}
          </div>
        )}

        {/* Time Animation View */}
        {activeTab === 'timeline' && (
          <div className="flex-1 flex">
            <div className="flex-1 relative">
              <MapContainer
                center={US_CENTER}
                zoom={DEFAULT_ZOOM}
                style={{ height: '100%', width: '100%' }}
              >
                <TileLayer
                  attribution='&copy; OpenStreetMap'
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                />
                
                {(timelineFeatures?.features || []).map((feature, idx) => {
                  const [lng, lat] = feature.geometry.coordinates;
                  const props = feature.properties;
                  const isSelected = selectedContracts.includes(props.contractId);
                  const color = getMarkerColor(props.competitivenessScore);
                  const size = getMarkerSize(props.annualValue);
                  const isExpiring = props.expiresThisMonth;
                  
                  return (
                    <CircleMarker
                      key={props.contractId || idx}
                      center={[lat, lng]}
                      radius={isExpiring ? size + 4 : size}
                      pathOptions={{
                        color: isExpiring ? '#FF521C' : (isSelected ? '#09409C' : color),
                        fillColor: color,
                        fillOpacity: isExpiring ? 0.9 : 0.7,
                        weight: isExpiring ? 3 : 2,
                      }}
                      eventHandlers={{
                        click: () => toggleContractSelection(props.contractId),
                      }}
                    >
                      <Popup>
                        <div className="min-w-[200px]">
                          <div className="font-semibold text-sm">{props.accountName}</div>
                          <div className="text-xs text-muted-foreground mb-2">{props.contractNumber}</div>
                          {isExpiring && (
                            <Badge className="mb-2 bg-red-100 text-red-800 text-[10px]">
                              <AlertTriangle className="w-3 h-3 mr-1" />
                              Expires this month
                            </Badge>
                          )}
                          <div className="space-y-1 text-xs">
                            <div className="flex justify-between">
                              <span>Value:</span>
                              <span className="font-medium">{formatCurrency(props.annualValue)}</span>
                            </div>
                            <div className="flex justify-between">
                              <span>Health:</span>
                              <span className={props.competitivenessScore >= 70 ? 'text-green-600' : props.competitivenessScore >= 50 ? 'text-yellow-600' : 'text-red-600'}>
                                {props.competitivenessScore}%
                              </span>
                            </div>
                          </div>
                        </div>
                      </Popup>
                    </CircleMarker>
                  );
                })}
              </MapContainer>

              {/* Timeline Controls */}
              <div className="absolute top-4 left-1/2 transform -translate-x-1/2 z-[1000] w-96">
                <TimeAnimationControls
                  onTimelineChange={handleTimelineChange}
                  isPlaying={isPlaying}
                  setIsPlaying={setIsPlaying}
                  currentMonth={currentMonth}
                  setCurrentMonth={setCurrentMonth}
                />
              </div>

              {/* Current Month Label */}
              {timelineLabel && (
                <div className="absolute top-4 right-4 z-[1000] bg-[#09409C] text-white px-4 py-2 rounded-lg shadow-lg">
                  <div className="text-lg font-bold">{timelineLabel}</div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Split Screen View */}
        {activeTab === 'split' && (
          <div className="flex-1">
            <SplitScreenMap 
              onViewDetails={handleViewDetails}
            />
          </div>
        )}

        {/* What-If Scenario View */}
        {activeTab === 'scenario' && (
          <div className="flex-1 flex">
            <div className="flex-1 relative">
              <MapContainer
                center={US_CENTER}
                zoom={DEFAULT_ZOOM}
                style={{ height: '100%', width: '100%' }}
              >
                <TileLayer
                  attribution='&copy; OpenStreetMap'
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                />
                <FitBoundsOnLoad features={currentFeatures} />
                
                {currentFeatures.map((feature, idx) => {
                  const [lng, lat] = feature.geometry.coordinates;
                  const props = feature.properties;
                  const isSelected = selectedContracts.includes(props.contractId);
                  const color = getMarkerColor(props.competitivenessScore);
                  const size = getMarkerSize(props.annualValue);
                  
                  return (
                    <CircleMarker
                      key={props.contractId || idx}
                      center={[lat, lng]}
                      radius={size}
                      pathOptions={{
                        color: isSelected ? '#09409C' : color,
                        fillColor: isSelected ? '#09409C' : color,
                        fillOpacity: isSelected ? 0.9 : 0.5,
                        weight: isSelected ? 3 : 2,
                      }}
                      eventHandlers={{
                        click: () => toggleContractSelection(props.contractId),
                      }}
                    >
                      <Popup>
                        <div className="min-w-[180px]">
                          <div className="flex items-center gap-2 mb-2">
                            <Checkbox 
                              checked={isSelected}
                              onCheckedChange={() => toggleContractSelection(props.contractId)}
                            />
                            <span className="font-semibold text-sm">{props.accountName}</span>
                          </div>
                          <div className="text-xs text-muted-foreground mb-1">{props.contractNumber}</div>
                          <div className="text-xs">
                            <span className="font-medium">{formatCurrency(props.annualValue)}</span>
                            <span className="text-muted-foreground"> • </span>
                            <span className={props.competitivenessScore >= 70 ? 'text-green-600' : props.competitivenessScore >= 50 ? 'text-yellow-600' : 'text-red-600'}>
                              {props.competitivenessScore}%
                            </span>
                          </div>
                        </div>
                      </Popup>
                    </CircleMarker>
                  );
                })}
              </MapContainer>

              {/* Selection Helper */}
              <div className="absolute top-4 left-4 z-[1000] bg-white/95 backdrop-blur-sm rounded-lg shadow-lg p-3 text-xs">
                <div className="font-medium mb-1">Click contracts to select</div>
                <div className="text-muted-foreground">
                  Selected contracts will be analyzed in the scenario panel
                </div>
              </div>
            </div>

            {/* Scenario Panel */}
            <div className="w-[420px] border-l bg-white overflow-auto">
              <WhatIfScenario 
                selectedContracts={selectedContracts}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
